import { msg } from "@lingui/core/macro";
import { type Component, createMemo, For, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { Transition } from "solid-transition-group";
import { Keyboard, X } from "lucide-solid";

import { useI18n } from "@/providers/i18n-provider";
import { GlassSurface } from "@/components/ui/glass-surface";
import { Button } from "@/components/ui/button";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import {
  useBinding,
  useCommandCatalog,
  useCommandHandler,
  useScope,
  useShortcuts,
} from "@/lib/shortcuts/solid";
import { useInteractionFlag } from "@/lib/interaction/solid";

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DisplayBinding {
  steps: { keys: string[] }[];
}

interface DisplayedShortcut {
  description: string;
  bindings: DisplayBinding[];
}

interface DisplayedCategory {
  title: string;
  shortcuts: DisplayedShortcut[];
}

export const ShortcutsModal: Component<ShortcutsModalProps> = (props) => {
  const { t } = useI18n();
  const { registry, format, platform } = useShortcuts();
  const catalog = useCommandCatalog();

  useScope("dialog", () => props.isOpen);
  useInteractionFlag({ "dialog.open": () => props.isOpen });
  useBinding({ command: COMMAND_IDS.DIALOG_CLOSE, keys: "Escape", scope: "dialog" });
  useCommandHandler(COMMAND_IDS.DIALOG_CLOSE, () => {
    if (props.isOpen) {
      props.onClose();
    }
  });

  const categories = createMemo<DisplayedCategory[]>(() => {
    const groups = catalog();
    const result: DisplayedCategory[] = [];
    for (const { category, commands } of groups) {
      const shortcuts: DisplayedShortcut[] = [];
      for (const cmd of commands) {
        const bindings = registry.bindingsFor(cmd.id);
        if (bindings.length === 0) {
          continue;
        }
        // Filter bindings by platform-availability (engine already filters at
        // dispatch time but the help modal should mirror what's reachable).
        const reachable = bindings.filter((b) => {
          if (b.platforms && !b.platforms.includes(platform.id)) {
            return false;
          }
          if (platform.reservedBindings.has(b.keys)) {
            return false;
          }
          return true;
        });
        if (reachable.length === 0) {
          continue;
        }
        shortcuts.push({
          description: t(cmd.titleKey),
          bindings: reachable.map((b) => format(b.keys)),
        });
      }
      if (shortcuts.length > 0) {
        result.push({ title: t(category), shortcuts });
      }
    }
    return result;
  });

  return (
    <Portal>
      <Transition name="fade-scale">
        <Show when={props.isOpen}>
          <div class="fixed inset-0 z-[150] flex items-center justify-center p-4">
            <button
              type="button"
              class="absolute inset-0 bg-black/60 backdrop-blur-[4px] transition-opacity duration-300 border-none cursor-default"
              onClick={() => {
                props.onClose();
              }}
              aria-label={t(msg`Close modal`)}
            />

            <GlassSurface
              class="relative w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200"
              borderEdge="accent"
            >
              <div class="flex items-center justify-between border-b border-border/20 px-6 py-4 shrink-0">
                <div class="flex items-center gap-3">
                  <div class="h-8 w-8 rounded-lg bg-accent/20 flex items-center justify-center text-primary">
                    <Keyboard class="h-5 w-5" />
                  </div>
                  <h2 class="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/80">
                    {t(msg`Keyboard Shortcuts`)}
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  class="h-8 w-8 rounded-lg text-muted-foreground hover:bg-muted/10 hover:text-foreground transition-all active:scale-95"
                  onClick={props.onClose}
                >
                  <X class="h-4 w-4" />
                </Button>
              </div>

              <div class="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-6 scrollbar-thin">
                <For each={categories()}>
                  {(category) => (
                    <div class="space-y-3">
                      <h3 class="text-xs font-bold uppercase tracking-wider text-primary/80 px-1 border-b border-border/10 pb-1.5">
                        {category.title}
                      </h3>
                      <div class="space-y-2">
                        <For each={category.shortcuts}>
                          {(shortcut) => (
                            <div class="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-muted/5 transition-colors">
                              <span class="text-xs text-muted-foreground font-medium pr-4">
                                {shortcut.description}
                              </span>
                              <div class="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                                <For each={shortcut.bindings}>
                                  {(binding, bIdx) => (
                                    <>
                                      <Show when={bIdx() > 0}>
                                        <span class="text-[9px] text-muted-foreground/30 px-0.5">
                                          /
                                        </span>
                                      </Show>
                                      <div class="flex items-center gap-1">
                                        <For each={binding.steps}>
                                          {(step, sIdx) => (
                                            <>
                                              <Show when={sIdx() > 0}>
                                                <span class="text-[10px] text-muted-foreground/40 font-semibold px-0.5">
                                                  then
                                                </span>
                                              </Show>
                                              <div class="flex items-center gap-0.5">
                                                <For each={step.keys}>
                                                  {(key, kIdx) => (
                                                    <>
                                                      <Show when={kIdx() > 0}>
                                                        <span class="text-[10px] text-muted-foreground/40 font-semibold px-0.5">
                                                          +
                                                        </span>
                                                      </Show>
                                                      <kbd class="h-5 inline-flex items-center justify-center rounded border border-border/60 bg-background px-1.5 font-mono text-[9px] font-bold text-foreground/80 shadow-sm">
                                                        {key}
                                                      </kbd>
                                                    </>
                                                  )}
                                                </For>
                                              </div>
                                            </>
                                          )}
                                        </For>
                                      </div>
                                    </>
                                  )}
                                </For>
                              </div>
                            </div>
                          )}
                        </For>
                      </div>
                    </div>
                  )}
                </For>
              </div>

              <div class="border-t border-border/20 px-6 py-4 shrink-0 bg-muted/10 flex items-center justify-between text-[10px] text-muted-foreground">
                <span>{t(msg`Press '?' on any screen to open this guide.`)}</span>
                <span>{t(msg`Close with 'Esc' or click outside.`)}</span>
              </div>
            </GlassSurface>
          </div>
        </Show>
      </Transition>
    </Portal>
  );
};
